import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Search } from "lucide-react";
import { NAV_LINKS } from "@/data/content";
import { SearchOverlay } from "@/components/SearchOverlay";

const UTILITY = [
  { label: "Subscribe", to: "/#newsletter", testId: "utility-subscribe-link" },
  { label: "Partner With Us", to: "/#partnership", testId: "utility-partner-link" },
  { label: "Support Our Work", to: "/#support", testId: "utility-support-link" },
];

const BridgeMark = () => (
  <svg width="34" height="25" viewBox="0 0 36 26" fill="none" aria-hidden="true" className="mt-0.5 shrink-0">
    <path d="M3 16.5 C 9.5 6.5, 26.5 6.5, 33 16.5" stroke="#C5A059" strokeWidth="1.5" />
    <line x1="9.5" y1="10.8" x2="9.5" y2="19" stroke="#C5A059" strokeWidth="1.5" />
    <line x1="18" y1="8.6" x2="18" y2="19" stroke="#C5A059" strokeWidth="1.5" />
    <line x1="26.5" y1="10.8" x2="26.5" y2="19" stroke="#C5A059" strokeWidth="1.5" />
    <line x1="3" y1="19.5" x2="33" y2="19.5" stroke="#C5A059" strokeWidth="1.5" />
  </svg>
);

const slugify = (label) => label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "");

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      <div className="bg-atlantic text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-2 lg:px-8">
          <p className="hidden text-[10px] uppercase tracking-[0.25em] text-white/55 md:block">
            One Atlantic. Shared Histories. Common Futures.
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs font-semibold tracking-wide">
            <button
              data-testid="utility-search-button"
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-1.5 text-white/80 transition-colors hover:text-gold"
            >
              <Search size={13} /> Search
            </button>
            {UTILITY.map((u) => (
              <Link key={u.label} data-testid={u.testId} to={u.to} className="text-white/80 transition-colors hover:text-gold">
                {u.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="border-b border-atlantic/10 bg-sand/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-5 py-4 lg:px-8">
          <Link to="/" data-testid="brand-logo-link" className="flex items-start gap-3">
            <BridgeMark />
            <span className="leading-none">
              <span className="block whitespace-nowrap font-serif text-xl font-bold tracking-tight text-atlantic xl:text-2xl">
                Atlantic Bridge
              </span>
              <span className="mt-1.5 block text-[10px] font-semibold uppercase tracking-[0.42em] text-gold">
                Institute
              </span>
            </span>
          </Link>
          <nav className="hidden items-center lg:flex lg:gap-3.5 xl:gap-6" aria-label="Primary navigation">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.label}
                data-testid={`nav-link-${slugify(l.label)}`}
                to={l.to}
                className="whitespace-nowrap font-medium text-atlantic/80 transition-colors hover:text-ocean lg:text-[11px] xl:text-[13px]"
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMenuOpen(!menuOpen)}
            className="text-atlantic lg:hidden"
            aria-label="Toggle navigation menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {menuOpen && (
          <nav data-testid="mobile-nav" className="border-t border-atlantic/10 bg-sand px-5 py-4 lg:hidden" aria-label="Mobile navigation">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.label}
                data-testid={`mobile-nav-link-${slugify(l.label)}`}
                to={l.to}
                onClick={() => setMenuOpen(false)}
                className="block border-b border-atlantic/5 py-3 text-sm font-medium text-atlantic/85 last:border-0"
              >
                {l.label}
              </Link>
            ))}
          </nav>
        )}
      </div>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}
